<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import AssistantStarterPacks from './AssistantStarterPacks.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
  showSubmit: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['submit']);

const PRODUCT_KEYS = [
  'after_quote',
  'ask_order',
  'collect',
  'ask_name',
  'ask_phone',
  'ask_address',
  'after_confirm',
  'close_script',
];

const SERVICE_KEYS = ['ask_area', 'ask_problem', 'ask_time', 'confirm_lead'];

const { t } = useI18n();

const state = reactive({
  scripts: {},
  exampleChats: [],
});

const extraScriptFields = computed(() => {
  const extras = props.assistant?.config?.business_brain?.extra_slots || [];
  return extras
    .filter(slot => slot?.key)
    .map(slot => ({
      key: `ask_extra_${slot.key}`,
      label: slot.label || slot.key,
    }));
});

const emptyScripts = () => {
  const scripts = {};
  [...PRODUCT_KEYS, ...SERVICE_KEYS].forEach(key => {
    scripts[key] = '';
  });
  extraScriptFields.value.forEach(field => {
    scripts[field.key] = '';
  });
  return scripts;
};

const updateStateFromAssistant = assistant => {
  const training = assistant?.config?.training || {};
  const stored = training.scripts || {};
  const brain = assistant?.config?.business_brain || {};
  const scripts = emptyScripts();
  Object.keys(scripts).forEach(key => {
    scripts[key] = stored[key] || '';
  });
  if (!scripts.close_script) {
    scripts.close_script = brain.close_script || '';
  }
  state.scripts = scripts;
  state.exampleChats = Array.isArray(training.example_chats)
    ? training.example_chats.map(chat => ({
        id: chat.id || `custom_${Date.now()}`,
        kind: chat.kind || 'correction',
        title: chat.title || '',
        customer: chat.customer || '',
        bad: chat.bad || '',
        good: chat.good || '',
      }))
    : [];
};

const addExampleChat = () => {
  state.exampleChats.push({
    id: `custom_${Date.now()}`,
    kind: 'correction',
    title: '',
    customer: '',
    bad: '',
    good: '',
  });
};

const removeExampleChat = index => {
  state.exampleChats.splice(index, 1);
};

const handleSubmit = () => {
  emit('submit', {
    config: {
      training: {
        version: 1,
        scripts: { ...state.scripts },
        example_chats: state.exampleChats.map(chat => ({
          id: chat.id,
          kind: chat.kind || 'correction',
          title: chat.title,
          customer: chat.customer,
          bad: chat.bad,
          good: chat.good,
        })),
      },
    },
  });
};

defineExpose({ submit: handleSubmit });

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true, deep: true }
);

watch(
  extraScriptFields,
  () => {
    extraScriptFields.value.forEach(field => {
      if (state.scripts[field.key] === undefined) {
        state.scripts[field.key] = '';
      }
    });
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <AssistantStarterPacks :assistant="assistant" />

    <section
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TRAINING.CARDS.PRODUCT') }}
      </h4>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TRAINING.CARDS.PRODUCT_HELP') }}
      </p>
      <TextArea
        v-for="key in PRODUCT_KEYS"
        :key="key"
        v-model="state.scripts[key]"
        :label="
          t(
            `CAPTAIN.ASSISTANTS.FORM.TRAINING.SCRIPTS.${key.toUpperCase()}.LABEL`
          )
        "
        :placeholder="
          t(
            `CAPTAIN.ASSISTANTS.FORM.TRAINING.SCRIPTS.${key.toUpperCase()}.PLACEHOLDER`
          )
        "
        :max-length="2000"
        auto-height
      />
    </section>

    <section
      v-if="extraScriptFields.length"
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TRAINING.CARDS.EXTRAS') }}
      </h4>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TRAINING.CARDS.EXTRAS_HELP') }}
      </p>
      <TextArea
        v-for="field in extraScriptFields"
        :key="field.key"
        v-model="state.scripts[field.key]"
        :label="
          t('CAPTAIN.ASSISTANTS.FORM.TRAINING.SCRIPTS.ASK_EXTRA.LABEL', {
            slot: field.label,
          })
        "
        :placeholder="
          t('CAPTAIN.ASSISTANTS.FORM.TRAINING.SCRIPTS.ASK_EXTRA.PLACEHOLDER', {
            slot: field.label,
          })
        "
        :max-length="2000"
        auto-height
      />
    </section>

    <section
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TRAINING.CARDS.SERVICE') }}
      </h4>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TRAINING.CARDS.SERVICE_HELP') }}
      </p>
      <TextArea
        v-for="key in SERVICE_KEYS"
        :key="key"
        v-model="state.scripts[key]"
        :label="
          t(
            `CAPTAIN.ASSISTANTS.FORM.TRAINING.SCRIPTS.${key.toUpperCase()}.LABEL`
          )
        "
        :placeholder="
          t(
            `CAPTAIN.ASSISTANTS.FORM.TRAINING.SCRIPTS.${key.toUpperCase()}.PLACEHOLDER`
          )
        "
        :max-length="2000"
        auto-height
      />
    </section>

    <section
      class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
    >
      <div class="flex items-center justify-between gap-4">
        <div class="flex flex-col gap-1">
          <h4 class="mb-0 text-base font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.TRAINING.CARDS.EXAMPLES') }}
          </h4>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.FORM.TRAINING.CARDS.EXAMPLES_HELP') }}
          </p>
        </div>
        <Button
          ghost
          xs
          :label="t('CAPTAIN.ASSISTANTS.FORM.TRAINING.EXAMPLES.ADD')"
          @click="addExampleChat"
        />
      </div>
      <p v-if="!state.exampleChats.length" class="mb-0 text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TRAINING.EXAMPLES.EMPTY') }}
      </p>
      <article
        v-for="(chat, index) in state.exampleChats"
        :key="chat.id"
        class="flex flex-col gap-4 rounded-xl bg-n-alpha-black2 p-4"
      >
        <Input
          v-model="chat.title"
          :label="t('CAPTAIN.ASSISTANTS.FORM.TRAINING.EXAMPLES.TITLE')"
          :placeholder="
            t('CAPTAIN.ASSISTANTS.FORM.TRAINING.EXAMPLES.TITLE_PLACEHOLDER')
          "
        />
        <TextArea
          v-model="chat.customer"
          :label="t('CAPTAIN.ASSISTANTS.FORM.TRAINING.EXAMPLES.CUSTOMER')"
          :placeholder="
            t('CAPTAIN.ASSISTANTS.FORM.TRAINING.EXAMPLES.CUSTOMER_PLACEHOLDER')
          "
          :max-length="2000"
          auto-height
        />
        <TextArea
          v-model="chat.bad"
          :label="t('CAPTAIN.ASSISTANTS.FORM.TRAINING.EXAMPLES.BAD')"
          :placeholder="
            t('CAPTAIN.ASSISTANTS.FORM.TRAINING.EXAMPLES.BAD_PLACEHOLDER')
          "
          :max-length="2000"
          auto-height
        />
        <TextArea
          v-model="chat.good"
          :label="t('CAPTAIN.ASSISTANTS.FORM.TRAINING.EXAMPLES.GOOD')"
          :placeholder="
            t('CAPTAIN.ASSISTANTS.FORM.TRAINING.EXAMPLES.GOOD_PLACEHOLDER')
          "
          :max-length="2000"
          auto-height
        />
        <div>
          <Button
            ghost
            ruby
            xs
            :label="t('CAPTAIN.ASSISTANTS.FORM.TRAINING.EXAMPLES.REMOVE')"
            @click="removeExampleChat(index)"
          />
        </div>
      </article>
    </section>

    <div v-if="showSubmit">
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
